/**
 * Console Interceptor - Monkey patches console methods to send logs to backend
 *
 * Intercepts console.log, console.debug, console.info, console.warn, and console.error
 * and forwards them to the LoggerService when debug mode is enabled.
 *
 * Features:
 * - Preserves original console behavior
 * - Automatic detection of caller context from stack trace
 * - Can be enabled/disabled dynamically
 * - Minimal performance impact
 */

import { cleanCircularReferences } from "../tools/CircularReferenceTools";
import { shouldSkipMemoryDiagnosticsForwarding } from "../tools/MemoryDiagnosticsLog.tools";
import { LoggerService } from "./LoggerService";

class ConsoleInterceptorClass {

    private isPatched: boolean = false;

    private originalConsole!: {
        "log":   typeof console.log;
        "debug": typeof console.debug;
        "info":  typeof console.info;
        "warn":  typeof console.warn;
        "error": typeof console.error;
    };

    constructor() {
        // Save original console methods
        this.originalConsole = {
            "log":   console.log.bind( console ),
            "debug": console.debug.bind( console ),
            "info":  console.info.bind( console ),
            "warn":  console.warn.bind( console ),
            "error": console.error.bind( console ),
        };
    }

    /**
     * Extract context from stack trace
     */
    private getCallerContext(): string {
        try {
            const stack = new Error().stack;
            if ( stack === undefined ) {
                return "Unknown";
            }

            const stackLines = stack.split( "\n" );

            // Skip first 3 lines (Error, getCallerContext, intercepted method)
            for ( let i = 3; i < stackLines.length; i++ ) {
                const line = stackLines[i];
                if ( line === undefined ) {
                    continue;
                }

                // Look for file name and location
                const match = line.match( /at\s+(?:(\w+)\.)?(\w+)?\s*\(?.+\/([^/]+):(\d+):(\d+)/ );
                if ( match !== null ) {
                    const [ , className, methodName, fileName ] = match;

                    if ( className !== undefined && methodName !== undefined ) {
                        return `${ className }.${ methodName }`;
                    } else if ( methodName !== undefined ) {
                        return methodName;
                    } else if ( fileName !== undefined ) {
                        // Remove .tsx, .ts, .jsx, .js extensions
                        return fileName.replace( /\.(tsx?|jsx?)$/, "" );
                    }
                }

                // Fallback: try to extract just the file name
                const simpleMatch = line.match( /\/([^/]+\.(?:tsx?|jsx?))/ );
                if ( simpleMatch !== null && simpleMatch[1] !== undefined ) {
                    return simpleMatch[1].replace( /\.(tsx?|jsx?)$/, "" );
                }
            }
        } catch {
            // Silently fail
        }

        return "Console";
    }

    /**
     * Format arguments for logging
     */
    private formatArgs( args: unknown[] ): {
        "message": string;
        "data"?:   unknown;
    } {
        if ( args.length === 0 ) {
            return { "message": "" };
        }

        const firstArg = args[0];
        let message: string;

        if ( typeof firstArg === "string" ) {
            message = firstArg;
        } else if ( firstArg instanceof Error ) {
            message = `${ firstArg.name }: ${ firstArg.message }`;
        } else {
            message = String( firstArg );
        }

        // Nettoyer les données avant de les envoyer
        let data: unknown = undefined;
        if ( args.length > 1 ) {
            const dataArgs = args.slice( 1 ).map( ( arg ) => cleanCircularReferences( arg, new WeakSet(), 0, 10 ) );

            /*
             * Si un seul élément, retourner l'objet directement (pas un tableau)
             * Si plusieurs éléments, retourner un tableau
             */
            data = dataArgs.length === 1 ? dataArgs[0] : dataArgs;
        }

        return {
            message,
            data,
        };
    }

    /**
     * Apply monkey patch to console methods
     */
    public patch(): void {
        if ( this.isPatched ) {
            return;
        }

        // Patch console.log
        console.log = ( ...args: unknown[] ) => {
            this.originalConsole.log( ...args );

            if ( shouldSkipMemoryDiagnosticsForwarding( args[0] ) ) {
                return;
            }

            // Always capture console.log, not just in debug mode
            const context = this.getCallerContext();
            const { message, data } = this.formatArgs( args );
            LoggerService.debug( message, context, data );
        };

        // Patch console.debug
        console.debug = ( ...args: unknown[] ) => {
            this.originalConsole.debug( ...args );

            if ( shouldSkipMemoryDiagnosticsForwarding( args[0] ) ) {
                return;
            }

            // Always capture console.debug, not just in debug mode
            const context = this.getCallerContext();
            const { message, data } = this.formatArgs( args );
            LoggerService.debug( message, context, data );
        };

        // Patch console.info
        console.info = ( ...args: unknown[] ) => {
            this.originalConsole.info( ...args );

            if ( shouldSkipMemoryDiagnosticsForwarding( args[0] ) ) {
                return;
            }

            // Always capture console.info
            const context = this.getCallerContext();
            const { message, data } = this.formatArgs( args );
            LoggerService.info( message, context, data );
        };

        // Patch console.warn
        console.warn = ( ...args: unknown[] ) => {
            this.originalConsole.warn( ...args );

            if ( shouldSkipMemoryDiagnosticsForwarding( args[0] ) ) {
                return;
            }

            // Always capture console.warn
            const context = this.getCallerContext();
            const { message, data } = this.formatArgs( args );
            LoggerService.warn( message, context, data );
        };

        // Patch console.error
        console.error = ( ...args: unknown[] ) => {
            this.originalConsole.error( ...args );

            // Errors are always sent, even in non-debug mode
            const context = this.getCallerContext();
            const { message, data } = this.formatArgs( args );
            LoggerService.error( message, context, data );
        };

        this.isPatched = true;
        this.originalConsole.log( "[ConsoleInterceptor] Console methods patched" );
    }

    /**
     * Restore original console methods
     */
    public unpatch(): void {
        if ( !this.isPatched ) {
            return;
        }

        console.log = this.originalConsole.log;
        console.debug = this.originalConsole.debug;
        console.info = this.originalConsole.info;
        console.warn = this.originalConsole.warn;
        console.error = this.originalConsole.error;

        this.isPatched = false;
        this.originalConsole.log( "[ConsoleInterceptor] Console methods restored" );
    }

    /**
     * Check if console is currently patched
     */
    public isActive(): boolean {
        return this.isPatched;
    }

    /**
     * Get original console methods (useful for debugging)
     */
    public getOriginalConsole() {
        return this.originalConsole;
    }

}

// Export singleton instance
export const ConsoleInterceptor = new ConsoleInterceptorClass();
